import React, { ReactElement, ReactNode, useEffect } from "react";
import { FormLabel, Grid, GridTypeMap, Tooltip, Typography } from "@mui/material";
import TranslationWrapper, { Translation } from "../../TranslationWrapper/TranslationWrapper";
import AuthenticationWrapper from "../../../auth/AuthenticationWrapper";
import PropTypes from "prop-types";
import WarningIcon from "@mui/icons-material/Warning";
import { Alert } from "@mui/lab";
import { withStyles } from "tss-react/mui";

const WarningTooltip = withStyles(Tooltip, (theme) => ({
  tooltip: {
    backgroundColor: theme.palette.warning.main,
  },
}));

const defaultGridSizes = {
  column1: { xs: 12, sm: 4 },
  column2: { xs: 12, sm: 8 },
};

type MuiSizes = Pick<GridTypeMap["props"], "xs" | "sm" | "md" | "lg" | "xl">;

export type FormFieldTwoColumnsParams = {
  label?: string | ReactNode;
  tooltip?: string | Element | JSX.Element;
  permissionKey?: string;
  name?: string;
  gridSizes: {
    column1?: MuiSizes;
    column2?: MuiSizes;
  };
  disabled?: boolean;
  children: ReactElement;
  warning?: {
    hasWarning: boolean;
    text: Translation;
  };
  noclone?: boolean;
} & Translation;

function FormFieldTwoColumns(props: FormFieldTwoColumnsParams) {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  useEffect(() => {
    let id: undefined | number;
    if (props.warning?.hasWarning) {
      setTooltipOpen(true);
      id = window.setTimeout(() => {
        setTooltipOpen(false);
      }, 3000);
    }

    return () => {
      window.clearTimeout(id);
    };
  }, [props.warning]);

  const handleClose = () => {
    setTooltipOpen(false);
  };

  const handleOpen = () => {
    setTooltipOpen(true);
  };

  const formLabel = (
    <FormLabel style={{ ...(props.tooltip ? { borderBottom: "1px dotted", cursor: "help" } : null) }}>
      {props.label ? (
        props.label
      ) : props.tKey ? (
        <TranslationWrapper tDefault={props.tDefault ?? props.tKey} tKey={props.tKey} />
      ) : null}
    </FormLabel>
  );
  return (
    <AuthenticationWrapper
      child={(write) => (
        <Grid
          container
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            margin: "8px 0",
          }}
        >
          <Grid
            item
            {...props.gridSizes.column1}
            style={{
              paddingRight: 32,
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              textAlign: "start",
            }}
          >
            {props.tooltip ? (
              <Tooltip title={<Typography variant={"body1"}>{props.tooltip}</Typography>}>{formLabel}</Tooltip>
            ) : (
              formLabel
            )}
            {props.warning && props.warning.hasWarning ? (
              <WarningTooltip
                onClose={handleClose}
                onOpen={handleOpen}
                open={tooltipOpen}
                title={
                  <Alert severity={"warning"}>
                    <TranslationWrapper {...props.warning.text} />
                  </Alert>
                }
              >
                <WarningIcon
                  sx={{
                    color: "#ff9800",
                    "&:hover": {
                      cursor: "help",
                    },
                  }}
                />
              </WarningTooltip>
            ) : null}
          </Grid>
          <Grid
            item
            {...props.gridSizes.column2}
            style={{ display: "flex", alignItems: "center", paddingRight: 32, flexWrap: "wrap", width: "100%" }}
          >
            {props.noclone
              ? props.children
              : Array.isArray(props.children)
                ? props.children.map((child, index) =>
                    index === 0 ? React.cloneElement(child, { disabled: !write || props.disabled, ...props }) : child
                  )
                : React.cloneElement(props.children, { disabled: !write || props.disabled, ...props })}
          </Grid>
        </Grid>
      )}
      permissionKey={props.permissionKey ?? props.name}
    />
  );
}

FormFieldTwoColumns.defaultProps = {
  gridSizes: { ...defaultGridSizes },
};

FormFieldTwoColumns.propTypes = {
  tooltip: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  label: PropTypes.oneOf([PropTypes.string, PropTypes.node]),
  tKey: PropTypes.string,
  tDefault: PropTypes.string,
  permissionKey: PropTypes.string,
  name: PropTypes.string,
  gridSizes: PropTypes.object,
};

export default FormFieldTwoColumns;
